import { useRef } from "react";
import { useParams } from "react-router-dom";
import {
  BrandingProvider,
  LeaderboardList,
  LeaderboardRow,
  LeaderboardShell,
  formatLapTime,
  leaderboardCarLabels,
  useOrientation,
} from "@vms/leaderboard-ui";
import type { LeaderboardEntry } from "@vms/types";
import {
  useBrandingTemplate,
  useEvent,
  useEventFastestSectors,
  useEventLeaderboard,
} from "../../api/leaderboards";

/**
 * Chrome-less hotlap leaderboard for cloud-ui — mirror of hub-ui's
 * `apps/hub-ui/src/pages/display/HotlapDisplay.tsx`. Mounted outside the
 * cloud-ui shell layout so it renders bare (no sidebar / topbar) and is
 * suitable for projecting on a venue TV — or pulling up on a phone over WAN.
 *
 * Real-time refresh: `useEventLeaderboard` and `useEventFastestSectors`
 * subscribe to the venue's WS room (via `useVenueSubscription` inside the
 * hooks) and invalidate on `entity-changed` envelopes after a lap-time delta
 * lands. Same UX as hub-ui inside a venue.
 */
export function HotlapDisplay() {
  const { eventId } = useParams<{ eventId: string }>();
  const { isLandscape } = useOrientation();
  const prevEntriesRef = useRef<LeaderboardEntry[]>([]);

  const { data: event } = useEvent(eventId);
  const venueId = event?.venueId ?? undefined;
  const { data: branding } = useBrandingTemplate(event?.brandingTemplateId ?? null);
  const { data: entries } = useEventLeaderboard(venueId, eventId);
  const { data: sectors } = useEventFastestSectors(venueId, eventId);

  const leaderboard = entries ?? [];

  // Show the car (model name only) only when 2+ distinct car models are present.
  const carLabels = leaderboardCarLabels(leaderboard, event?.carsAvailable);

  // Detect lap-time / position improvements for the pulse animation.
  const prevMap = new Map(
    prevEntriesRef.current.map((e) => [`${e.driver.id}:${e.car}`, e]),
  );
  const pulseSet = new Set<string>();
  for (const entry of leaderboard) {
    const key = `${entry.driver.id}:${entry.car}`;
    const prev = prevMap.get(key);
    if (prev && (entry.bestLapTimeMs < prev.bestLapTimeMs || entry.position < prev.position)) {
      pulseSet.add(key);
    }
  }
  if (leaderboard.length > 0) {
    prevEntriesRef.current = leaderboard;
  }

  return (
    <BrandingProvider branding={branding}>
      <LeaderboardShell
        title={event?.name ?? "Leaderboard"}
        subtitle={event?.trackName ?? event?.track}
        mode="hotlap"
      >
        {leaderboard.length === 0 ? (
          <div className="flex items-center justify-center h-full text-gray-500 text-lg">
            No lap times recorded yet.
          </div>
        ) : (
          <LeaderboardList
            totalCount={leaderboard.length}
            renderEntry={(index) => {
              const entry = leaderboard[index];
              const key = `${entry.driver.id}:${entry.car}`;
              return (
                <LeaderboardRow
                  position={entry.position}
                  driverName={`${entry.driver.firstName} ${entry.driver.lastName}`}
                  companyName={entry.driver.companyName || undefined}
                  car={carLabels[index]}
                  lapTime={formatLapTime(entry.bestLapTimeMs)}
                  sectors={{
                    s1: entry.bestLapSector1Ms,
                    s2: entry.bestLapSector2Ms,
                    s3: entry.bestLapSector3Ms,
                  }}
                  fastestSectors={sectors ?? undefined}
                  showSectors={isLandscape}
                  pulse={pulseSet.has(key)}
                />
              );
            }}
          />
        )}
      </LeaderboardShell>
    </BrandingProvider>
  );
}
